/**
 * @fileoverview Browser storage helpers for the auth session and pending two-factor challenge.
 * @description Wraps localStorage access for the pieces of auth-related state that outlive a
 * single page session, so no other module reads or writes these keys directly. Only the access
 * token is stored here — the refresh token is an httpOnly cookie now, invisible to (and never
 * held by) this app's own JS; see response.interceptor.ts's silent-refresh flow. localStorage (not
 * sessionStorage or in-memory) is a deliberate choice to keep users signed in across tab/browser
 * restarts, matching this app's existing pattern; it means a stolen access token via XSS is valid
 * until it expires, which is mitigated by the app's other XSS defenses rather than by storage
 * choice — see the request/response interceptors for the token's actual attach/refresh/revoke
 * lifecycle. The signed-in user is persisted alongside the access token, populated from a
 * follow-up GET /identity/me call once MFA verification succeeds (see useVerifyTwoFactor.ts) since
 * the verify response itself no longer carries role/profile fields. The pending two-factor
 * challenge is persisted so refreshing mid-verification doesn't lose it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authConfig } from '@/config/auth-config'

import type { AuthUser, TwoFactorChallenge } from '@/shared/lib/auth/auth.types'

/**
 * @description Reads the persisted access token, if any.
 * @returns {string | null} The stored access token, or null when not signed in.
 */
export function getStoredAccessToken(): string | null {
  return localStorage.getItem(authConfig.accessTokenStorageKey)
}

/**
 * @description Persists the access token after a successful login or refresh.
 * @param {string} accessToken - The access token returned by the identity backend.
 */
export function setStoredAccessToken(accessToken: string): void {
  localStorage.setItem(authConfig.accessTokenStorageKey, accessToken)
}

/**
 * @description Removes the persisted access token, e.g. on logout or a failed refresh.
 */
export function clearStoredAccessToken(): void {
  localStorage.removeItem(authConfig.accessTokenStorageKey)
}

/**
 * @description Reads the persisted signed-in user, if any.
 * @returns {AuthUser | null} The stored user, or null when not signed in or the stored value is
 * malformed.
 */
export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(authConfig.userStorageKey)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

/**
 * @description Persists the signed-in user after a successful login.
 * @param {AuthUser} user - The user derived from the post-verify GET /identity/me call.
 */
export function setStoredUser(user: AuthUser): void {
  localStorage.setItem(authConfig.userStorageKey, JSON.stringify(user))
}

/**
 * @description Removes the persisted user, e.g. on logout or a failed refresh.
 */
export function clearStoredUser(): void {
  localStorage.removeItem(authConfig.userStorageKey)
}

/**
 * @description Reads the pending two-factor challenge left by a successful credentials check, if
 * any, so a page refresh mid-verification doesn't bounce the user back to the login form.
 * @returns {TwoFactorChallenge | null} The pending challenge, or null when there isn't one or the
 * stored value is malformed.
 */
export function getStoredTwoFactorChallenge(): TwoFactorChallenge | null {
  const raw = localStorage.getItem(authConfig.twoFactorChallengeStorageKey)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as TwoFactorChallenge
  } catch {
    return null
  }
}

/**
 * @description Persists the pending two-factor challenge after a successful credentials check (or
 * a resend, which rotates its token and bumps its expiry).
 * @param {TwoFactorChallenge} challenge - The challenge awaiting verification.
 */
export function setStoredTwoFactorChallenge(challenge: TwoFactorChallenge): void {
  localStorage.setItem(authConfig.twoFactorChallengeStorageKey, JSON.stringify(challenge))
}

/**
 * @description Removes the pending two-factor challenge, e.g. once verification succeeds or the
 * user backs out to the login form.
 */
export function clearStoredTwoFactorChallenge(): void {
  localStorage.removeItem(authConfig.twoFactorChallengeStorageKey)
}
